/*
  Homepage trending on social (sections/trending-social.liquid).

  Progressive enhancement over plain markup. Without this file the feature
  image links to the video on TikTok, the row scrolls by touch or trackpad,
  and the product cards still buy (assets/best-sellers.js).

  - Row: arrows scroll it a card at a time and are shown only while it
    overflows (assets/media-rows.js).
  - Feature video: a click swaps the image for TikTok's embed player
    (tiktok.com/player/v1), so no TikTok code loads until someone asks for
    the video. Starting it stops any other video on the page
    (media-rows.js), and it is thrown away once it leaves the screen, which
    is what stops the sound.
*/
(() => {
  if (customElements.get('trending-social')) return;
  const rows = window.chellaMediaRows;
  if (!rows) return;

  class TrendingSocial extends HTMLElement {
    /** @type {AbortController | null} */
    #abort = null;
    /** @type {IntersectionObserver | null} */
    #playerObserver = null;
    /** Passed to claimPlayer, so another section's video can stop this one. */
    #stop = () => this.#stopVideo();

    connectedCallback() {
      this.#abort = new AbortController();
      const { signal } = this.#abort;
      this.#setUpRow(signal);
      this.#setUpVideo(signal);

      // Theme editor: selecting a product block brings its card into view.
      this.addEventListener(
        'shopify:block:select',
        (event) => {
          if (!(event.target instanceof Element)) return;
          event.target.scrollIntoView({ block: 'nearest', inline: 'start' });
        },
        { signal }
      );
    }

    disconnectedCallback() {
      this.#abort?.abort();
      this.#playerObserver?.disconnect();
      this.#stopVideo();
    }

    /** @param {AbortSignal} signal */
    #setUpRow(signal) {
      const track = this.querySelector('[data-trending-track]');
      if (!(track instanceof HTMLElement)) return;
      rows.attachRow({
        track,
        controls: this.querySelector('[data-trending-controls]'),
        arrows: Array.from(this.querySelectorAll('[data-trending-scroll]')),
        direction: (arrow) => Number(arrow.dataset.trendingScroll),
        signal,
      });
    }

    /** @param {AbortSignal} signal */
    #setUpVideo(signal) {
      const poster = this.querySelector('[data-trending-play]');
      if (!(poster instanceof HTMLElement)) return;

      this.#playerObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => !entry.isIntersecting)) this.#stopVideo();
      });

      poster.addEventListener(
        'click',
        (event) => {
          // Let modified clicks open TikTok in a new tab as usual.
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          event.preventDefault();
          this.#playVideo(poster);
        },
        { signal }
      );
    }

    /** @param {HTMLElement} poster */
    #playVideo(poster) {
      const media = poster.closest('[data-trending-media]');
      if (!(media instanceof HTMLElement) || !poster.dataset.embed) return;

      const frame = rows.createPlayer(poster, 'trending-social__frame', 'data-trending-frame');
      poster.hidden = true;
      media.append(frame);
      frame.focus({ preventScroll: true });
      this.#playerObserver?.observe(media);
      rows.claimPlayer(this.#stop);
    }

    #stopVideo() {
      const frame = this.querySelector('[data-trending-frame]');
      if (!frame) return;
      const media = frame.parentElement;
      if (media) this.#playerObserver?.unobserve(media);
      rows.releasePlayer(this.#stop);

      const hadFocus = frame === document.activeElement;
      frame.remove();
      const poster = this.querySelector('[data-trending-play]');
      if (poster instanceof HTMLElement) {
        poster.hidden = false;
        if (hadFocus) poster.focus({ preventScroll: true });
      }
    }
  }

  customElements.define('trending-social', TrendingSocial);
})();
